/**
 * App-wide role hierarchy — see CLAUDE.md §3.
 *
 * Seven tiers, ordered by authority (high → low). A user can only add or
 * assign roles **strictly below** their own. Sub-roles are restricted
 * subsets of their parent's permissions by default; the parent may grant
 * additional permissions explicitly via `role_permission_grants`.
 *
 * `production_test` is the factory test role: a production-team tester logs
 * into the mobile app's Test Mode to exercise a freshly built controller. It
 * is off the end-user hierarchy — only Autonomix staff (`admin`/`super_admin`)
 * may assign or revoke it, and it can never assign roles to anyone else.
 */
import { z } from 'zod';
export const AppRoleSchema = z.enum([
    'super_admin',
    'admin',
    'installer',
    'main_user',
    'sub_main_user',
    'member',
    'sub_member',
    'production_test',
]);
/**
 * Authority ranks. Lower number = higher authority. A caller with rank
 * `r` can add/assign roles with rank `> r` (strictly below). `production_test`
 * is off-hierarchy (it cannot add anyone and cannot be added by an end user).
 */
export const AppRoleRank = {
    super_admin: 0,
    admin: 1,
    installer: 2,
    main_user: 3,
    sub_main_user: 4,
    member: 5,
    sub_member: 6,
    production_test: 99,
};
/** True if `caller` may add/assign `target`. */
export function canAssignRole(caller, target) {
    // A production tester never assigns roles to anyone.
    if (caller === 'production_test')
        return false;
    // The factory test role is off-hierarchy: only Autonomix staff may grant or
    // revoke it (end users can neither create nor remove a tester).
    if (target === 'production_test') {
        return caller === 'super_admin' || caller === 'admin';
    }
    return AppRoleRank[caller] < AppRoleRank[target];
}
/**
 * Permission strings used by `role_permission_grants`. A grant row makes a
 * permission available to a specific grantee beyond the default restricted
 * set for their role.
 */
export const AppPermissionSchema = z.enum([
    'members.add',
    'members.remove',
    'devices.configure',
    'devices.transfer',
    'floors.configure',
]);
//# sourceMappingURL=roles.js.map